import { z } from "zod";
import { audioTrackSchema } from "./audio.js";
import { captionsSchema } from "./caption.js";
import { projectFontSchema } from "./font.js";
import { fpsSchema } from "./primitives.js";
import { bodySceneSchema, bookendSceneSchema } from "./scene.js";
import { transitionSchema } from "./transition.js";

/**
 * The timeline is the single source of truth. It has to be complete enough that
 * Remotion can render from it with no provider calls and no database access —
 * the editor preview and the final render consume this exact document.
 */
export const timelineSchema = z
  .object({
    version: z.literal(1).default(1),
    fps: fpsSchema.default(30),
    width: z.number().int().min(320).max(7680).default(1920),
    height: z.number().int().min(240).max(4320).default(1080),
    sections: z.object({
      head: bookendSceneSchema.optional(),
      body: bodySceneSchema,
      end: bookendSceneSchema.optional(),
    }),
    transitions: z
      .object({ headToBody: transitionSchema.optional(), bodyToEnd: transitionSchema.optional() })
      .default({}),
    audio: z.array(audioTrackSchema).max(64).default([]),
    captions: captionsSchema.default({}),
    /**
     * Fonts uploaded to this project. The bundled library needs no entry here —
     * it ships with the application — so this is only the ones that came with
     * the project and have to travel with it.
     */
    fonts: z.array(projectFontSchema).max(24).default([]),
    meta: z
      .object({
        projectId: z.string().max(64).optional(),
        projectName: z.string().max(160).optional(),
        generatedAt: z.string().max(40).optional(),
      })
      .default({}),
  })
  .superRefine((timeline, ctx) => {
    if (timeline.sections.head && timeline.sections.head.type !== "head") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["sections", "head"],
        message: "head section must have type 'head'",
      });
    }
    if (timeline.sections.end && timeline.sections.end.type !== "end") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["sections", "end"],
        message: "end section must have type 'end'",
      });
    }
    // A transition consumes time from both neighbours; it can never be longer
    // than the shorter of the two or the sections would render out of order.
    const head = timeline.sections.head;
    const body = timeline.sections.body;
    const end = timeline.sections.end;
    const headToBody = timeline.transitions.headToBody;
    if (head?.enabled && headToBody && headToBody.duration > Math.min(head.duration, body.duration)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["transitions", "headToBody", "duration"],
        message: "transition is longer than the sections it joins",
      });
    }
    const bodyToEnd = timeline.transitions.bodyToEnd;
    if (end?.enabled && bodyToEnd && bodyToEnd.duration > Math.min(body.duration, end.duration)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["transitions", "bodyToEnd", "duration"],
        message: "transition is longer than the sections it joins",
      });
    }
    for (const [index, effect] of body.focus.entries()) {
      if (effect.start + effect.duration > body.duration + 0.001) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["sections", "body", "focus", index],
          message: "focus effect runs past the end of the BODY section",
        });
      }
    }
  });

export type Timeline = z.infer<typeof timelineSchema>;

export const validateTimeline = (value: unknown): Timeline => timelineSchema.parse(value);
export const safeValidateTimeline = (value: unknown) => timelineSchema.safeParse(value);
